import { useEffect, useRef, useState, useCallback, type RefObject } from 'react';
import type { IChartApi, ISeriesApi } from 'lightweight-charts';
import { createLineToolsPlugin, type ILineToolsPlugin } from 'lightweight-charts-line-tools-core';
import registerLinesPlugin from 'lightweight-charts-line-tools-lines';
import { LineToolRectangle } from 'lightweight-charts-line-tools-rectangle';
import { LineToolCircle } from 'lightweight-charts-line-tools-circle';
import registerFibRetracementPlugin from 'lightweight-charts-line-tools-fib-retracement';
import registerTextPlugin from 'lightweight-charts-line-tools-text';

const SAVE_DEBOUNCE_MS = 400;
const storageKey = (symbol: string, timeframe: string): string =>
  `pm:drawings:${symbol || 'unknown'}:${timeframe || 'default'}`;

interface Params {
  chartRef: RefObject<IChartApi | null>;
  seriesRef: RefObject<ISeriesApi<'Candlestick'> | null>;
  ready: boolean;
  enabled: boolean;
  symbol: string;
  timeframe: string;
}

export interface ChartDrawingApi {
  activeTool: string | null;
  setTool: (toolType: string | null) => void;
  clearAll: () => void;
}

/**
 * Wires the difurious line-tools-core plugin (vendored, MPL-2.0) onto an
 * existing lightweight-charts v5 chart + series, with per-symbol/timeframe
 * persistence in localStorage.
 *
 * The plugin provides the TradingView-like interactive UX itself — click-to-draw
 * with live preview, draggable anchor handles, shift-constraints — so this hook
 * only registers the tool packages, activates a tool on demand (`addLineTool`),
 * restores/saves state (`importLineTools`/`exportLineTools`), and cleans up.
 */
export function useChartDrawing({
  chartRef,
  seriesRef,
  ready,
  enabled,
  symbol,
  timeframe,
}: Params): ChartDrawingApi {
  const pluginRef = useRef<ILineToolsPlugin | null>(null);
  const [activeTool, setActiveToolState] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !ready) return;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;

    let plugin: ILineToolsPlugin;
    try {
      plugin = createLineToolsPlugin(chart, series);
      // The vendored per-tool packages each bundle their own copy of the core
      // types, so the structural generic signatures don't unify across package
      // boundaries — register against a loosened view. Runtime is correct.
      const reg = plugin as unknown as {
        registerLineTool: (type: string, toolClass: new (...args: unknown[]) => unknown) => void;
      };
      // `lines` covers trend/ray/h-line/vertical/cross/arrow/callout/etc.
      (registerLinesPlugin as (p: unknown) => void)(plugin);
      (registerFibRetracementPlugin as (p: unknown) => void)(plugin);
      (registerTextPlugin as (p: unknown) => void)(plugin);
      reg.registerLineTool('Rectangle', LineToolRectangle as never);
      reg.registerLineTool('Circle', LineToolCircle as never);
    } catch {
      return;
    }
    pluginRef.current = plugin;

    // Restore saved drawings.
    try {
      const raw = localStorage.getItem(storageKey(symbol, timeframe));
      if (raw) plugin.importLineTools(raw);
    } catch {
      /* corrupt payload — start clean */
    }

    // Debounced persistence. `afterEdit` fires after a tool is created or
    // edited — save then, and drop back to the cursor once one is placed.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const persist = (): void => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          localStorage.setItem(storageKey(symbol, timeframe), plugin.exportLineTools());
        } catch {
          /* quota / serialization — non-fatal */
        }
      }, SAVE_DEBOUNCE_MS);
    };
    const afterEdit = (): void => {
      persist();
      setActiveToolState(null);
    };
    plugin.subscribeLineToolsAfterEdit(afterEdit);

    return () => {
      if (timer) clearTimeout(timer);
      plugin.unsubscribeLineToolsAfterEdit(afterEdit);
      try {
        plugin.destroy();
      } catch {
        /* chart already removed */
      }
      pluginRef.current = null;
      setActiveToolState(null);
    };
  }, [enabled, ready, symbol, timeframe, chartRef, seriesRef]);

  const setTool = useCallback((toolType: string | null) => {
    const plugin = pluginRef.current;
    if (!plugin) return;
    if (toolType) {
      plugin.addLineTool(toolType as Parameters<ILineToolsPlugin['addLineTool']>[0]);
    }
    setActiveToolState(toolType);
  }, []);

  const clearAll = useCallback(() => {
    pluginRef.current?.removeAllLineTools();
  }, []);

  return { activeTool, setTool, clearAll };
}
